import React, { useState } from "react";
import { Sparkles, Edit3, Trash2, ArrowUpRight, ArrowDownRight, Minus, Loader2, Check, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// FIN-4 — InvestimentoRow.
// Variant table (md+) | compact (<md, lista de cards verticais).
// CTA voz + edit + delete no hover/sempre em mobile.
// Edição inline de current_value: click no valor abre input rápido.

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

const fmtBRLShort = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL", maximumFractionDigits: 2,
});

const TYPE_META = {
  acoes:              { label: "Ações",            tone: "info"      },
  FII:                { label: "FII",              tone: "outline"   },
  fundo_acoes:        { label: "Fundo Ações",      tone: "info"      },
  fundo_multimercado: { label: "Fundo Multim.",    tone: "outline"   },
  CDB:                { label: "CDB",              tone: "success"   },
  LCI:                { label: "LCI",              tone: "success"   },
  LCA:                { label: "LCA",              tone: "success"   },
  tesouro:            { label: "Tesouro",          tone: "success"   },
  cripto:             { label: "Cripto",           tone: "warning"   },
  outros:             { label: "Outros",           tone: "outline"   },
};

function _toCents(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function GainBadge({ gain, gainPct }) {
  const v = Number(gain || 0);
  const p = Number(gainPct || 0);
  if (v === 0) {
    return (
      <Badge variant="outline" className="text-caption inline-flex items-center gap-1">
        <Minus className="w-3 h-3" /> {fmtBRL(0)}
      </Badge>
    );
  }
  const positive = v > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <Badge
      variant={positive ? "success" : "danger"}
      className="text-caption inline-flex items-center gap-1"
    >
      <Icon className="w-3 h-3" />
      {positive ? "+" : "−"}{fmtBRL(Math.abs(v))} · {positive ? "+" : "−"}{Math.abs(p).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%
    </Badge>
  );
}

function fmtQty(qty) {
  // Cripto: até 8 decimais; outros: 0 ou 4 decimais
  const n = Number(qty || 0);
  if (Number.isInteger(n)) return n.toLocaleString("pt-BR");
  // Tira trailing zeros mas mantém decimais relevantes
  return n.toLocaleString("pt-BR", { maximumFractionDigits: 8 });
}

export default function InvestimentoRow({
  inv,
  onTalk,
  onEdit,
  onDelete,
  onUpdateCurrent,        // (id, newValue) => Promise<>
  variant = "table",
}) {
  const meta = TYPE_META[inv.type] || TYPE_META.outros;
  const [editingValue, setEditingValue] = useState(false);
  const [valueInput, setValueInput] = useState("");
  const [saving, setSaving] = useState(false);

  const startEditValue = (e) => {
    e.stopPropagation();
    setValueInput(String(inv.current_value || 0).replace(".", ","));
    setEditingValue(true);
  };

  const cancelEditValue = (e) => {
    e?.stopPropagation();
    setEditingValue(false);
  };

  const saveEditValue = async (e) => {
    e?.stopPropagation();
    const newValue = _toCents(valueInput);
    if (newValue == null || newValue < 0) {
      setEditingValue(false);
      return;
    }
    if (newValue === inv.current_value) {
      setEditingValue(false);
      return;
    }
    setSaving(true);
    try {
      await onUpdateCurrent?.(inv.id, newValue);
      setEditingValue(false);
    } catch (_) {
      // toast tratado pelo caller
    } finally {
      setSaving(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") saveEditValue(e);
    if (e.key === "Escape") cancelEditValue(e);
  };

  const valueCell = editingValue ? (
    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        type="text"
        inputMode="decimal"
        autoFocus
        value={valueInput}
        onChange={(e) => setValueInput(e.target.value)}
        onKeyDown={handleKey}
        onBlur={saveEditValue}
        className="w-24 px-2 py-0.5 bg-surface-card-hover border border-brand-cyan/40 rounded text-foreground text-caption font-mono outline-none focus:ring-1 focus:ring-brand-cyan"
        aria-label="Novo valor atual"
        data-testid={`invest-row-${inv.id}-edit-value-input`}
      />
      <button
        type="button"
        onClick={saveEditValue}
        disabled={saving}
        className="p-1 rounded text-brand-green hover:bg-brand-green/10"
        aria-label="Confirmar"
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </button>
      <button
        type="button"
        onClick={cancelEditValue}
        className="p-1 rounded text-foreground-muted hover:bg-surface-card-hover"
        aria-label="Cancelar"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  ) : (
    <button
      type="button"
      onClick={startEditValue}
      className="font-mono text-foreground tabular-nums hover:text-brand-cyan hover:underline underline-offset-2 transition-colors"
      title="Clique pra atualizar valor"
      data-testid={`invest-row-${inv.id}-edit-value`}
    >
      {fmtBRLShort(inv.current_value)}
    </button>
  );

  if (variant === "compact") {
    return (
      <div
        className="group/row relative glass-card rounded-xl p-4 hover:border-brand-cyan/30 transition-all"
        data-testid={`invest-row-${inv.id}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-foreground font-semibold truncate">{inv.name}</p>
            {inv.ticker && (
              <p className="text-micro text-foreground-muted">{inv.ticker} · {inv.institution}</p>
            )}
          </div>
          <Badge variant={meta.tone} className="text-micro">{meta.label}</Badge>
        </div>

        <div className="flex items-center justify-between text-caption mb-2">
          <span className="text-foreground-muted">{fmtQty(inv.quantity)}× {fmtBRLShort(inv.purchase_price)}</span>
          <span className="font-mono font-semibold text-foreground tabular-nums">{fmtBRL(inv.current_total)}</span>
        </div>

        <div className="flex items-center justify-between">
          <GainBadge gain={inv.gain_loss} gainPct={inv.gain_loss_pct} />
          <div className="flex items-center gap-1">
            {onTalk && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onTalk(inv); }}
                className="p-1.5 rounded-md bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/20 transition-colors"
                aria-label="Falar com Nexo sobre este investimento"
                data-testid={`invest-row-${inv.id}-talk`}
              >
                <Sparkles className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onEdit?.(inv); }}
              className="p-1.5 rounded-md text-foreground-muted hover:bg-surface-card-hover hover:text-foreground transition-colors"
              aria-label="Editar"
              data-testid={`invest-row-${inv.id}-edit`}
            >
              <Edit3 className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete?.(inv); }}
              className="p-1.5 rounded-md text-status-error/70 hover:bg-status-error/10 hover:text-status-error transition-colors"
              aria-label="Apagar"
              data-testid={`invest-row-${inv.id}-delete`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Table row
  return (
    <tr
      className="group/row border-b border-subtle/50 transition-all duration-fast hover:bg-surface-card-hover"
      data-testid={`invest-row-${inv.id}`}
    >
      {/* Nome + Ticker */}
      <td className="py-3 pr-3">
        <div className="min-w-0">
          <p className="text-foreground font-semibold truncate text-caption">{inv.name}</p>
          {inv.ticker && (
            <p className="text-micro text-foreground-muted">{inv.ticker}</p>
          )}
        </div>
      </td>

      {/* Tipo */}
      <td className="py-3 px-3">
        <Badge variant={meta.tone} className="text-micro">{meta.label}</Badge>
      </td>

      {/* Quantidade */}
      <td className="py-3 px-3 font-mono text-caption tabular-nums whitespace-nowrap">
        {fmtQty(inv.quantity)}
      </td>

      {/* Preço médio */}
      <td className="py-3 px-3 font-mono text-caption text-foreground-muted tabular-nums whitespace-nowrap">
        {fmtBRLShort(inv.purchase_price)}
      </td>

      {/* Valor atual unitário (editável inline) */}
      <td className="py-3 px-3 whitespace-nowrap">
        {valueCell}
      </td>

      {/* Total atual */}
      <td className="py-3 px-3 font-mono font-bold text-caption text-foreground tabular-nums whitespace-nowrap">
        {fmtBRL(inv.current_total)}
      </td>

      {/* Ganho/Perda */}
      <td className="py-3 px-3 whitespace-nowrap">
        <GainBadge gain={inv.gain_loss} gainPct={inv.gain_loss_pct} />
      </td>

      {/* Instituição + ações */}
      <td className="py-3 pl-3 text-right relative whitespace-nowrap">
        <span className="text-caption text-foreground-muted">{inv.institution}</span>
        <div className="absolute top-1/2 -translate-y-1/2 right-1 flex items-center gap-1 opacity-0 group-hover/row:opacity-100 transition-opacity">
          {onTalk && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onTalk(inv); }}
              className="p-1.5 rounded-md bg-brand-cyan/10 text-brand-cyan hover:bg-brand-cyan/25 transition-colors"
              title="Falar com Nexo"
              aria-label="Falar com Nexo sobre este investimento"
              data-testid={`invest-row-${inv.id}-talk`}
            >
              <Sparkles className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onEdit?.(inv); }}
            className="p-1.5 rounded-md bg-surface-card-hover text-foreground-muted hover:text-foreground transition-colors"
            title="Editar"
            aria-label="Editar"
            data-testid={`invest-row-${inv.id}-edit`}
          >
            <Edit3 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete?.(inv); }}
            className="p-1.5 rounded-md bg-status-error/10 text-status-error/80 hover:bg-status-error/20 hover:text-status-error transition-colors"
            title="Apagar"
            aria-label="Apagar"
            data-testid={`invest-row-${inv.id}-delete`}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
